export interface UploadToS3Options {
  /** Callback for upload progress (0-100) */
  onProgress?: (progress: number) => void;
}

/** User-facing message for uploads that failed because the connection dropped. */
export const NETWORK_ERROR_MESSAGE =
  'The upload was interrupted by a network problem. Check your connection and retry.';

/** Delay before automatically retrying an upload interrupted by a network problem. */
export const NETWORK_RETRY_DELAY_MS = 1000;

/**
 * Custom error class for S3 upload failures.
 *
 * Separates the user-facing message from raw technical details,
 * so callers can show friendly messages while still reporting full context to Sentry.
 */
export class S3UploadError extends Error {
  /** Raw S3 error code extracted from the XML response (e.g. 'RequestTimeout') */
  readonly s3ErrorCode: string | null;
  /** HTTP status code returned by S3 */
  readonly httpStatus: number;
  /** Raw response text from S3 (may contain XML) */
  readonly rawResponse: string;

  constructor({
    userMessage,
    s3ErrorCode,
    httpStatus,
    rawResponse
  }: {
    userMessage: string;
    s3ErrorCode: string | null;
    httpStatus: number;
    rawResponse: string;
  }) {
    super(userMessage);
    this.name = 'S3UploadError';
    this.s3ErrorCode = s3ErrorCode;
    this.httpStatus = httpStatus;
    this.rawResponse = rawResponse;
  }
}

/**
 * Parse an S3 XML error response and return a user-friendly message and the raw error code.
 */
const parseS3ErrorResponse = (
  responseText: string,
  status: number
): { userMessage: string; errorCode: string | null } => {
  let errorCode: string | null = null;

  try {
    const parser = new DOMParser();
    const xml = parser.parseFromString(responseText, 'text/xml');

    errorCode = xml.querySelector('Code')?.textContent ?? null;
  } catch {
    // XML parsing failed — fall through to generic message
  }

  return { userMessage: getS3UserMessage(errorCode, status), errorCode };
};

/**
 * Map an S3 error code and HTTP status to a user-friendly message.
 */
const getS3UserMessage = (errorCode: string | null, status: number): string => {
  switch (errorCode) {
    case 'RequestTimeout':
    case 'RequestTimeTooSkewed':
      return NETWORK_ERROR_MESSAGE;
    case 'SlowDown':
    case 'ServiceUnavailable':
      return 'Upload service is temporarily unavailable. Please try again later.';
    case 'EntityTooLarge':
    case 'MaxMessageLengthExceeded':
      return 'File is too large to upload.';
    case 'AccessDenied':
    case 'InvalidAccessKeyId':
    case 'SignatureDoesNotMatch':
    case 'ExpiredToken':
      return 'Upload authorization failed. Please refresh the page and try again.';
    default:
      if (status >= 500) {
        return 'Upload service is temporarily unavailable. Please try again later.';
      }

      return 'Upload failed. Please try again.';
  }
};

/**
 * Whether an upload failure is a transient connection problem worth retrying as-is.
 *
 * Status 0 means the request never got a response (connection dropped, offline);
 * RequestTimeout means S3 gave up waiting for the body. Neither says anything about
 * the request itself, unlike the other S3 errors.
 */
const isNetworkError = (error: unknown): boolean =>
  error instanceof S3UploadError &&
  (error.httpStatus === 0 || error.s3ErrorCode === 'RequestTimeout');

/**
 * Upload a file directly to S3 using a presigned POST URL.
 *
 * An upload interrupted by a network problem is retried once automatically, since
 * those are usually transient and the presigned POST is still valid.
 *
 * @param uploadUrl - The S3 URL to POST to
 * @param uploadFields - The fields to include in the form data (from presigned POST)
 * @param file - The file to upload
 * @param options - Optional settings including progress callback
 * @returns Promise that resolves when upload is complete
 * @throws S3UploadError if upload fails — use `.message` for user-facing text and other
 *   properties for Sentry context
 */
export const uploadToS3 = async (
  uploadUrl: string,
  uploadFields: Record<string, string>,
  file: File,
  options?: UploadToS3Options
): Promise<void> => {
  try {
    await attemptUploadToS3(uploadUrl, uploadFields, file, options);
  } catch (error) {
    if (!isNetworkError(error)) throw error;

    await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
    await attemptUploadToS3(uploadUrl, uploadFields, file, options);
  }
};

/**
 * Make a single POST of the file to S3.
 */
const attemptUploadToS3 = (
  uploadUrl: string,
  uploadFields: Record<string, string>,
  file: File,
  options?: UploadToS3Options
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();

    // Add all presigned fields first (order matters for S3)
    Object.entries(uploadFields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add the file last (required by S3)
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (options?.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);

          options.onProgress!(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        const { userMessage, errorCode } = parseS3ErrorResponse(
          xhr.responseText,
          xhr.status
        );

        reject(
          new S3UploadError({
            userMessage,
            s3ErrorCode: errorCode,
            httpStatus: xhr.status,
            rawResponse: xhr.responseText
          })
        );
      }
    });

    xhr.addEventListener('error', () => {
      reject(
        new S3UploadError({
          userMessage: NETWORK_ERROR_MESSAGE,
          s3ErrorCode: null,
          httpStatus: 0,
          rawResponse: ''
        })
      );
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    xhr.open('POST', uploadUrl);
    xhr.send(formData);
  });
};
